/**
 * WebGPU Compute Shader Accelerator for Voxel CSG
 *
 * SUB-REGION DISPATCH: Only processes voxels within the tool's AABB.
 * For a D30 tool on 2.6mm grid: ~3K voxels dispatched vs 67M full grid.
 *
 * Architecture:
 * - Flat packed 3D grid on GPU (4 voxels per u32, byte-packed)
 * - Sub-region dispatch: CPU computes tool AABB → voxel index range → small dispatch
 * - Thread gid maps to sub-region local coord → global voxel index → packed word
 * - Batch mode: 256 swept segments per dispatch (all share same sub-region AABB)
 */

import { VoxelToolGeometry } from './VoxelCSGEngine';

// ── Type Definitions ────────────────────────────────────────────────────────

export interface GPUComputeResult {
  modifiedVoxels: number;
  executionTime: number;
  success: boolean;
}

// ── Shader Code (WGSL) ──────────────────────────────────────────────────────

/**
 * Sub-region batch removal shader.
 * Each thread handles one voxel within the sub-region AABB.
 * Sub-region defined by (startX,startY,startZ) + (sizeX,sizeY,sizeZ).
 * Thread gid → local 3D coord → global 3D coord → packed buffer word.
 */
const SUBREGION_BATCH_SHADER = /* wgsl */ `
struct Grid {
  resX: u32,
  resY: u32,
  resZ: u32,
  voxelSize: f32,
  originX: f32,
  originY: f32,
  originZ: f32,
  numPositions: u32,
  // Sub-region bounds (voxel indices)
  startX: u32,
  startY: u32,
  startZ: u32,
  sizeX: u32,
  sizeY: u32,
  sizeZ: u32,
  totalSubVoxels: u32,
  _pad: u32,
}

struct ToolGeom {
  dirX: f32, dirY: f32, dirZ: f32, _pad0: f32,
  diameter: f32,
  cornerRadius: f32,
  length: f32,
  toolType: u32,
}

@group(0) @binding(0) var<storage, read_write> voxels: array<atomic<u32>>;
@group(0) @binding(1) var<uniform> grid: Grid;
@group(0) @binding(2) var<uniform> toolGeom: ToolGeom;
@group(0) @binding(3) var<storage, read> positions: array<f32>;

fn isPointInsideTool(px: f32, py: f32, pz: f32, cx: f32, cy: f32, cz: f32) -> bool {
  let dx = px - cx;
  let dy = py - cy;
  let dz = pz - cz;
  let radius = toolGeom.diameter * 0.5;
  let axial = dx * toolGeom.dirX + dy * toolGeom.dirY + dz * toolGeom.dirZ;
  let distSq = dx*dx + dy*dy + dz*dz;
  let radialSq = max(0.0, distSq - axial * axial);
  let radial = sqrt(radialSq);

  switch toolGeom.toolType {
    case 0u: {
      return radial <= radius && axial >= 0.0 && axial <= toolGeom.length;
    }
    case 1u: {
      if (axial >= -radius && axial < 0.0) {
        return radialSq + axial * axial <= radius * radius;
      }
      return axial >= 0.0 && axial <= toolGeom.length && radial <= radius;
    }
    case 2u: {
      if (axial >= toolGeom.cornerRadius && axial <= toolGeom.length) {
        return radial <= radius;
      }
      if (axial >= 0.0 && axial < toolGeom.cornerRadius) {
        if (radial <= radius - toolGeom.cornerRadius) { return true; }
        let dr = radial - (radius - toolGeom.cornerRadius);
        let da = axial - toolGeom.cornerRadius;
        return dr*dr + da*da <= toolGeom.cornerRadius * toolGeom.cornerRadius;
      }
      return false;
    }
    default: {
      return radial <= radius && axial >= 0.0 && axial <= toolGeom.length;
    }
  }
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  // Each thread handles one voxel in the sub-region
  // Supports 2D dispatch: linear index = gid.x + gid.y * (dispatchX * 256)
  // For 1D dispatch (gid.y=0), this reduces to gid.x
  let localIdx = gid.x + gid.y * 65535u * 256u;
  if (localIdx >= grid.totalSubVoxels) { return; }

  // Local 3D coordinate within sub-region
  let sizeXY = grid.sizeX * grid.sizeY;
  let lz = localIdx / sizeXY;
  let lrem = localIdx % sizeXY;
  let ly = lrem / grid.sizeX;
  let lx = lrem % grid.sizeX;

  // Global 3D coordinate
  let gx = grid.startX + lx;
  let gy = grid.startY + ly;
  let gz = grid.startZ + lz;

  // Global linear voxel index
  let globalIdx = gx + gy * grid.resX + gz * grid.resX * grid.resY;

  // Packed storage: 4 voxels per u32
  let wordIdx = globalIdx / 4u;
  let bytePos = globalIdx % 4u;

  // Read packed word and check if this voxel is already empty
  let word = atomicLoad(&voxels[wordIdx]);
  let byteVal = (word >> (bytePos * 8u)) & 0xFFu;
  if (byteVal == 0u) { return; } // Already empty

  // World position (voxel center)
  let px = grid.originX + (f32(gx) + 0.5) * grid.voxelSize;
  let py = grid.originY + (f32(gy) + 0.5) * grid.voxelSize;
  let pz = grid.originZ + (f32(gz) + 0.5) * grid.voxelSize;

  // Check against all swept segments
  let numPos = grid.numPositions;
  for (var p = 0u; p < numPos; p++) {
    let base = p * 6u;
    let sax = positions[base];
    let say = positions[base + 1u];
    let saz = positions[base + 2u];
    let sbx = positions[base + 3u];
    let sby = positions[base + 4u];
    let sbz = positions[base + 5u];

    // Swept: closest point on segment A→B
    let abx = sbx - sax;
    let aby = sby - say;
    let abz = sbz - saz;
    let segLenSq = abx*abx + aby*aby + abz*abz;

    var cx: f32; var cy: f32; var cz: f32;
    if (segLenSq < 1e-10) {
      cx = sax; cy = say; cz = saz;
    } else {
      let t = clamp(
        ((px - sax)*abx + (py - say)*aby + (pz - saz)*abz) / segLenSq,
        0.0, 1.0
      );
      cx = sax + t * abx;
      cy = say + t * aby;
      cz = saz + t * abz;
    }

    if (isPointInsideTool(px, py, pz, cx, cy, cz)) {
      // Atomically clear this voxel's byte
      let mask = ~(0xFFu << (bytePos * 8u));
      atomicAnd(&voxels[wordIdx], mask);
      return;
    }
  }
}
`;

// ── Main GPU Compute Class ──────────────────────────────────────────────────

export class VoxelGPUCompute {
  private device: GPUDevice | null = null;
  private initialized = false;

  // Buffers
  private voxelBuffer: GPUBuffer | null = null;
  private gridUniformBuffer: GPUBuffer | null = null;
  private toolUniformBuffer: GPUBuffer | null = null;
  private positionsBuffer: GPUBuffer | null = null;

  // Pipeline
  private pipeline: GPUComputePipeline | null = null;
  private bindGroup: GPUBindGroup | null = null;

  // Grid info
  private gridResolution: [number, number, number] = [0, 0, 0];
  private voxelSize = 1.0;
  private origin: [number, number, number] = [0, 0, 0];
  private totalVoxels = 0;
  private totalWords = 0;

  // Batch buffer config
  private readonly MAX_BATCH_POSITIONS = 256;

  // Staging for readback
  private stagingBuffer: GPUBuffer | null = null;
  private stagingMapped = false;

  // Pre-allocated uniform data (avoid GC in hot path)
  private gridUniformData: ArrayBuffer;
  private gridU32: Uint32Array;
  private gridF32: Float32Array;
  private toolUniformData: ArrayBuffer;
  private toolF32: Float32Array;
  private toolU32: Uint32Array;
  // Pre-allocated single-sweep position buffer
  private singleSweptPos = new Float32Array(6);
  // Pre-allocated solid init data (set after createBuffers)
  private solidInitData: Uint32Array | null = null;

  constructor() {
    // Grid uniform: 16 x u32/f32 = 64 bytes
    this.gridUniformData = new ArrayBuffer(64);
    this.gridU32 = new Uint32Array(this.gridUniformData);
    this.gridF32 = new Float32Array(this.gridUniformData);
    // Tool uniform: 8 x f32 = 32 bytes
    this.toolUniformData = new ArrayBuffer(32);
    this.toolF32 = new Float32Array(this.toolUniformData);
    this.toolU32 = new Uint32Array(this.toolUniformData);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  async initialize(
    resX: number, resY: number, resZ: number,
    voxelSize: number,
    origin: [number, number, number]
  ): Promise<boolean> {
    if (!(navigator as any).gpu) {
      console.warn('[VoxelGPU] WebGPU not available');
      return false;
    }

    try {
      const adapter = await (navigator as any).gpu.requestAdapter({ powerPreference: 'high-performance' });
      if (!adapter) { console.warn('[VoxelGPU] No WebGPU adapter'); return false; }

      const adapterMaxBuffer = (adapter.limits as any).maxBufferSize || 268435456;
      this.device = await adapter.requestDevice({
        requiredFeatures: [],
        requiredLimits: {
          maxBufferSize: adapterMaxBuffer,
          maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
          maxComputeWorkgroupSizeX: 256,
        },
      });

      if ('lost' in this.device && typeof (this.device as any).lost?.then === 'function') {
        (this.device as any).lost.then((info: any) => {
          console.error('[VoxelGPU] Device lost:', info);
          this.initialized = false;
        });
      }

      this.gridResolution = [resX, resY, resZ];
      this.voxelSize = voxelSize;
      this.origin = origin;
      this.totalVoxels = resX * resY * resZ;
      this.totalWords = Math.ceil(this.totalVoxels / 4);

      await this.createBuffers();
      await this.createPipeline();

      this.initialized = true;
      // Init complete: sub-region mode ready
      return true;

    } catch (error) {
      console.error('[VoxelGPU] Init failed:', error);
      return false;
    }
  }

  private async createBuffers(): Promise<void> {
    if (!this.device) return;
    const bufferSize = this.totalWords * 4;

    // Voxel data buffer (packed: 4 voxels per u32)
    this.voxelBuffer = this.device.createBuffer({
      size: bufferSize,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
    });

    // Initialize all solid (keep reference for fast reset)
    this.solidInitData = new Uint32Array(this.totalWords);
    this.solidInitData.fill(0xFFFFFFFF);
    this.device.queue.writeBuffer(this.voxelBuffer, 0, this.solidInitData);

    // Grid uniform (64 bytes)
    this.gridUniformBuffer = this.device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    // Tool uniform (32 bytes)
    this.toolUniformBuffer = this.device.createBuffer({
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    // Positions storage (256 segments x 6 floats x 4 bytes)
    this.positionsBuffer = this.device.createBuffer({
      size: this.MAX_BATCH_POSITIONS * 6 * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    // Staging for readback
    this.stagingBuffer = this.device.createBuffer({
      size: bufferSize,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
  }

  private async createPipeline(): Promise<void> {
    if (!this.device) return;

    const module = this.device.createShaderModule({ code: SUBREGION_BATCH_SHADER });
    this.pipeline = this.device.createComputePipeline({
      layout: 'auto',
      compute: { module, entryPoint: 'main' },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUB-REGION DISPATCH
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Compute voxel AABB for a batch of swept segments + tool geometry.
   * Returns [startX, startY, startZ, endX, endY, endZ] in voxel indices.
   */
  private computeSubRegion(
    positions: Float32Array, numPositions: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): [number, number, number, number, number, number] {
    const radius = tool.diameter / 2;
    const tipExtend = tool.type === 'ball' ? radius : 0;
    const rx = this.gridResolution[0], ry = this.gridResolution[1], rz = this.gridResolution[2];
    const vs = this.voxelSize;
    const ox = this.origin[0], oy = this.origin[1], oz = this.origin[2];

    // Find world-space AABB of all swept segments + tool envelope
    let wMinX = Infinity, wMinY = Infinity, wMinZ = Infinity;
    let wMaxX = -Infinity, wMaxY = -Infinity, wMaxZ = -Infinity;

    for (let i = 0; i < numPositions; i++) {
      const base = i * 6;
      const ax = positions[base], ay = positions[base + 1], az = positions[base + 2];
      const bx = positions[base + 3], by = positions[base + 4], bz = positions[base + 5];

      // Tool extends along dir by length (shank) and opposite by tipExtend (ball)
      const shankAx = ax + dirX * tool.length, shankAy = ay + dirY * tool.length, shankAz = az + dirZ * tool.length;
      const shankBx = bx + dirX * tool.length, shankBy = by + dirY * tool.length, shankBz = bz + dirZ * tool.length;
      const tipAx = ax - dirX * tipExtend, tipAy = ay - dirY * tipExtend, tipAz = az - dirZ * tipExtend;
      const tipBx = bx - dirX * tipExtend, tipBy = by - dirY * tipExtend, tipBz = bz - dirZ * tipExtend;

      wMinX = Math.min(wMinX, ax, bx, shankAx, shankBx, tipAx, tipBx);
      wMaxX = Math.max(wMaxX, ax, bx, shankAx, shankBx, tipAx, tipBx);
      wMinY = Math.min(wMinY, ay, by, shankAy, shankBy, tipAy, tipBy);
      wMaxY = Math.max(wMaxY, ay, by, shankAy, shankBy, tipAy, tipBy);
      wMinZ = Math.min(wMinZ, az, bz, shankAz, shankBz, tipAz, tipBz);
      wMaxZ = Math.max(wMaxZ, az, bz, shankAz, shankBz, tipAz, tipBz);
    }

    // Expand by tool radius
    wMinX -= radius; wMinY -= radius; wMinZ -= radius;
    wMaxX += radius; wMaxY += radius; wMaxZ += radius;

    // Convert to voxel indices and clamp
    const startX = Math.max(0, Math.floor((wMinX - ox) / vs));
    const startY = Math.max(0, Math.floor((wMinY - oy) / vs));
    const startZ = Math.max(0, Math.floor((wMinZ - oz) / vs));
    const endX = Math.min(rx - 1, Math.ceil((wMaxX - ox) / vs));
    const endY = Math.min(ry - 1, Math.ceil((wMaxY - oy) / vs));
    const endZ = Math.min(rz - 1, Math.ceil((wMaxZ - oz) / vs));

    return [startX, startY, startZ, endX, endY, endZ];
  }

  /**
   * Batch remove: process multiple swept segments, dispatching ONLY
   * the sub-region of voxels that the tool AABB covers.
   */
  removeBatchGPU(
    positions: Float32Array,
    numPositions: number,
    tool: VoxelToolGeometry,
    dirX = 0, dirY = 0, dirZ = 1
  ): void {
    if (!this.initialized || !this.device || !this.pipeline || !this.voxelBuffer) return;
    if (numPositions <= 0) return;
    const actualCount = Math.min(numPositions, this.MAX_BATCH_POSITIONS);

    // Compute sub-region AABB
    const [sx, sy, sz, ex, ey, ez] = this.computeSubRegion(positions, actualCount, tool, dirX, dirY, dirZ);
    const sizeX = ex - sx + 1;
    const sizeY = ey - sy + 1;
    const sizeZ = ez - sz + 1;
    if (sizeX <= 0 || sizeY <= 0 || sizeZ <= 0) return;
    const totalSubVoxels = sizeX * sizeY * sizeZ;

    // Write grid uniform (sub-region info included)
    const gu = this.gridU32;
    const gf = this.gridF32;
    const rx = this.gridResolution[0], ry = this.gridResolution[1], rz = this.gridResolution[2];
    gu[0] = rx; gu[1] = ry; gu[2] = rz;
    gf[3] = this.voxelSize;
    gf[4] = this.origin[0]; gf[5] = this.origin[1]; gf[6] = this.origin[2];
    gu[7] = actualCount;
    gu[8] = sx; gu[9] = sy; gu[10] = sz;
    gu[11] = sizeX; gu[12] = sizeY; gu[13] = sizeZ;
    gu[14] = totalSubVoxels;
    gu[15] = 0;
    this.device.queue.writeBuffer(this.gridUniformBuffer!, 0, this.gridUniformData);

    // Write tool uniform
    const tf = this.toolF32;
    const tu = this.toolU32;
    tf[0] = dirX; tf[1] = dirY; tf[2] = dirZ; tf[3] = 0;
    tf[4] = tool.diameter; tf[5] = tool.cornerRadius; tf[6] = tool.length;
    tu[7] = this.toolTypeToInt(tool.type);
    this.device.queue.writeBuffer(this.toolUniformBuffer!, 0, this.toolUniformData);

    // Upload positions
    this.device.queue.writeBuffer(this.positionsBuffer!, 0, positions, 0, actualCount * 6);

    // Create bind group (lazy, invalidated on initializeSolid)
    if (!this.bindGroup) {
      this.bindGroup = this.device.createBindGroup({
        layout: this.pipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.voxelBuffer } },
          { binding: 1, resource: { buffer: this.gridUniformBuffer! } },
          { binding: 2, resource: { buffer: this.toolUniformBuffer! } },
          { binding: 3, resource: { buffer: this.positionsBuffer! } },
        ],
      });
    }

    // Dispatch: one thread per sub-region voxel
    // Sub-region dispatch is typically small (few thousand voxels),
    // but guard against edge cases with 2D dispatch if needed
    const workgroups = Math.ceil(totalSubVoxels / 256);
    const commandEncoder = this.device.createCommandEncoder();
    const pass = commandEncoder.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    if (workgroups <= 65535) {
      pass.dispatchWorkgroups(workgroups);
    } else {
      // Shouldn't happen with sub-region, but safety net
      const dispatchX = 65535;
      const dispatchY = Math.ceil(workgroups / 65535);
      pass.dispatchWorkgroups(dispatchX, dispatchY);
    }
    pass.end();
    this.device.queue.submit([commandEncoder.finish()]);
  }

  /**
   * Single swept removal (A→B) - delegates to batch with 1 entry
   */
  removeSweptGPU(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX = 0, dirY = 0, dirZ = 1
  ): void {
    const pos = this.singleSweptPos;
    pos[0] = ax; pos[1] = ay; pos[2] = az;
    pos[3] = bx; pos[4] = by; pos[5] = bz;
    this.removeBatchGPU(pos, 1, tool, dirX, dirY, dirZ);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // READBACK
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Read voxel data back from GPU as Uint8Array.
   * Each byte: 0=empty, 0xFF=solid.
   */
  async readVoxelData(): Promise<Uint8Array | null> {
    if (!this.initialized || !this.device || !this.voxelBuffer || !this.stagingBuffer) return null;
    if (this.stagingMapped) return null;

    try {
      this.stagingMapped = true;
      const byteSize = this.totalWords * 4;

      const commandEncoder = this.device.createCommandEncoder();
      commandEncoder.copyBufferToBuffer(this.voxelBuffer, 0, this.stagingBuffer, 0, byteSize);
      this.device.queue.submit([commandEncoder.finish()]);

      await this.stagingBuffer.mapAsync(GPUMapMode.READ);
      const mappedData = new Uint8Array(this.stagingBuffer.getMappedRange());
      const result = new Uint8Array(this.totalVoxels);
      result.set(mappedData.subarray(0, this.totalVoxels));
      this.stagingBuffer.unmap();
      this.stagingMapped = false;
      return result;

    } catch (error) {
      console.error('[VoxelGPU] Readback failed:', error);
      this.stagingMapped = false;
      return null;
    }
  }

  /** Write all voxels as solid (uses pre-allocated buffer) */
  initializeSolid(): void {
    if (!this.device || !this.voxelBuffer || !this.solidInitData) return;
    this.device.queue.writeBuffer(this.voxelBuffer, 0, this.solidInitData);
    this.bindGroup = null;
  }

  /** Flush GPU queue */
  async flush(): Promise<void> {
    if (!this.device) return;
    if (typeof (this.device.queue as any).onSubmittedWorkDone === 'function') {
      await (this.device.queue as any).onSubmittedWorkDone();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  private toolTypeToInt(type: string): number {
    switch (type) {
      case 'flat': return 0;
      case 'ball': return 1;
      case 'bull': return 2;
      default: return 0;
    }
  }

  isInitialized(): boolean { return this.initialized; }

  /** Expose GPU device for shared pipelines (e.g. MarchingCubesGPU) */
  getDevice(): GPUDevice | null { return this.device; }

  /** Expose voxel storage buffer for shared access (e.g. MarchingCubesGPU) */
  getVoxelBuffer(): GPUBuffer | null { return this.voxelBuffer; }

  /** Total packed u32 words in voxel buffer */
  getTotalWords(): number { return this.totalWords; }

  /** Total voxels (resX * resY * resZ) */
  getTotalVoxels(): number { return this.totalVoxels; }

  getGridInfo() {
    return {
      resX: this.gridResolution[0],
      resY: this.gridResolution[1],
      resZ: this.gridResolution[2],
      voxelSize: this.voxelSize,
      origin: [...this.origin] as [number, number, number],
    };
  }

  dispose(): void {
    this.voxelBuffer?.destroy();
    this.gridUniformBuffer?.destroy();
    this.toolUniformBuffer?.destroy();
    this.positionsBuffer?.destroy();
    this.stagingBuffer?.destroy();
    if (this.device && typeof (this.device as any).destroy === 'function') {
      (this.device as any).destroy();
    }
    this.initialized = false;
    this.bindGroup = null;
    this.solidInitData = null;
  }
}

// ── Factory Function ────────────────────────────────────────────────────────

export async function createVoxelGPUCompute(
  resX: number, resY: number, resZ: number,
  voxelSize: number,
  origin: [number, number, number]
): Promise<VoxelGPUCompute | null> {
  const compute = new VoxelGPUCompute();
  const success = await compute.initialize(resX, resY, resZ, voxelSize, origin);
  if (!success) { compute.dispose(); return null; }
  return compute;
}
